/* eslint-disable no-underscore-dangle */
import { createAction } from '@reduxjs/toolkit';
import type { RewardState } from '../reducers/reward';
import { components } from '../schema/docs/api/admin';
import type { AppThunk } from '../store/configureStore';
import { throwOnError } from './utils';

type Reward = RewardState['reward'];
type Receivers = RewardState['receivers'];
type CreateBulkTransactionsResponse = components['schemas']['BulkTransaction'];

export const SET_UPDATE_REWARD = createAction<Reward>('SET_UPDATE_REWARD');
export const INIT_REWARD_RECEIVERS = createAction<void>('INIT_REWARD_RECEIVERS');
export const GET_REWARD_RECEIVERS = createAction<Receivers>('GET_REWARD_RECEIVERS');

// anti-pattern: should fix this
export const getRewards =
  (query = ''): AppThunk =>
  () =>
    fetch(`/api/messaging-operations?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    }).then(throwOnError);

export const createReward = (postData: Record<string, unknown> = {}) =>
  fetch('/api/messaging-operations', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json();
  });

export const initRewardReceivers = (): AppThunk => (dispatch) => dispatch(INIT_REWARD_RECEIVERS());

export const getRewardReceivers =
  (query = ''): AppThunk =>
  (dispatch) =>
    fetch(`/api/messaging-operations/receivers?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then((response) => response.json())
      .then((receivers) => dispatch(GET_REWARD_RECEIVERS(receivers)));

export const fetchRewardReceivers = (query: string) =>
  fetch(`/api/messaging-operations/receivers?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);

export const getRewardReceiversById = (messageOperationUUID: string) =>
  fetch(`/api/messaging-operations/receivers?messaging_operation_id=${messageOperationUUID}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then((response) => response.json());

export const setUpdateReward =
  (reward: Reward): AppThunk =>
  (dispatch) =>
    dispatch(SET_UPDATE_REWARD(reward));

export const updateReward = (id: string, postData: Record<string, unknown> = {}) =>
  fetch(`/api/messaging-operations/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json();
  });

export const createBulkTransactions = (postData: Record<string, unknown> = {}) =>
  fetch('/api/transactions/bulk', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<CreateBulkTransactionsResponse>);

export const createBulkTransactionsV2 = (postData: Record<string, unknown> = {}) =>
  fetch('/api/v2/transactions/bulk', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<CreateBulkTransactionsResponse>);

export const getBulkTransations = (query = '') =>
  fetch(`/api/bulk-transactions?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);

export const getTransactionJob = (id: string, query = '') =>
  fetch(`/api/bulk-transactions/${id}/jobs?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);

export const cancelBulkTransaction = (id: string) =>
  fetch(`/api/bulk-transactions/${id}/cancel`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError);

export const resumeBulkTransaction = (id: string) =>
  fetch(`/api/bulk-transactions/${id}/resume`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError);

export const cancelMessagingOperation = (id: string) =>
  fetch(`/api/messaging-operations/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(throwOnError);
